import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/admin';
import { Observable, of } from 'rxjs';
import { PermissionService } from '../../../../core/authorization/permission';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { Discount } from '../../../../shared/models/discount';
import { DiscountFormData } from '../../../../shared/models/discount-form-data';
import { DiscountForm } from '../discount-form/discount-form';


@Component({
	selector: 'app-discounts',
    standalone: true,
	imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, FormsModule, DatePipe],
	templateUrl: './discounts.html',
	styleUrl: './discounts.css'
})
export class Discounts implements OnInit, AfterViewInit {
	dataSource       : MatTableDataSource<Discount> = new MatTableDataSource<Discount>([]);
    displayedColumns : string[] = ['name', 'defaultAmount', 'calculationStrategy', 'assignmentType', 'status', 'createdAt', 'actions'];
    canCreateDiscount$: Observable<boolean>;
    canEditDiscount$  : Observable<boolean>;
    canDeleteDiscount$: Observable<boolean>;

    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
        this.canCreateDiscount$ = this.permissionService.hasPermission('discounts.create');
        this.canEditDiscount$   = this.permissionService.hasPermission('discounts.update');
        this.canDeleteDiscount$ = of(false);

        this.dataSource.filterPredicate = (data: Discount, filter: string) => {
            const searchString = filter.trim().toLowerCase();
            const nameMatch    = data.name.toLowerCase().includes(searchString);
            const amountMatch  = data.defaultAmount?.toString().includes(searchString) ?? false;
            const statusMatch  = (data.status ? 'activo' : 'inactivo').includes(searchString);
            const assignmentTypeDisplayMatch = this.getAssignmentTypeDisplayName(data.assignmentType).toLowerCase().includes(searchString);

            return nameMatch || amountMatch || statusMatch || assignmentTypeDisplayMatch;
        };
    }

    ngOnInit(): void {
        this.loadDiscounts();
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    async loadDiscounts(): Promise<void> {
        const discounts = await this.adminService.getAllDiscounts();

        if(discounts) {
            this.dataSource.data = discounts;
        }
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if(this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    async onCreateDiscount(): Promise<void> {
        const dialogRef = this.dialog.open(DiscountForm, {
            width: '700px',
            disableClose: true,
            data: null
        });

        dialogRef.afterClosed().subscribe(async (result: DiscountFormData | undefined) => {
            if(result) {
                const success = await this.adminService.createDiscount(result); 

                if(success) {
                    this.loadDiscounts();
                }
            }
        });
    }

    async onEditDiscount(discount: Discount): Promise<void> {
        const formData: DiscountFormData = {
            id                  : discount.id,
            name                : discount.name,
            defaultAmount       : discount.defaultAmount,
            calculationStrategy : discount.calculationStrategy,
            assignmentType      : discount.assignmentType,
            assignedCriteria    : discount.assignedCriteria,
            status              : discount.status
        };

        const dialogRef = this.dialog.open(DiscountForm, {
            width: '700px',
            disableClose: true,
            data: formData
        });

        dialogRef.afterClosed().subscribe(async (result: DiscountFormData | undefined) => {
            if(result) {
                const success = await this.adminService.updateDiscount(result); 

                if(success) {
                    this.loadDiscounts();
                }
            }
        });
    }

    async onDeleteDiscount(id: string): Promise<void> {
        if(window.confirm('¿Estás seguro de que quieres eliminar este descuento? Esta acción es irreversible.')) {
            const success = await this.adminService.deleteDiscount(id);

            if(success) {
                this.loadDiscounts();
            }
        }
    }

    getAssignmentTypeDisplayName(type: Discount['assignmentType']): string {
        switch (type) {
            case 'GLOBAL': return 'Global';
            case 'PARTNER': return 'Cliente';
            case 'ENTITY': return 'Entidad';
            case 'ROUTE': return 'Ruta';
            case 'BUS': return 'Bus';
            case 'PARTNER_ENTITY': return 'Cliente y Entidad';
            case 'ROUTE_ENTITY': return 'Ruta y Entidad';
            case 'PARTNER_ROUTE': return 'Cliente y Ruta';
            default: return type;
        }
    }
}
